<template>
  <div class="login-page">
    <!-- 背景装饰 -->
    <div class="background-decoration">
      <div class="gradient-bg"></div>
      <div class="floating-particles">
        <div class="particle" v-for="i in 20" :key="i"></div>
      </div>
      <div class="geometric-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
      </div>
    </div>

    <!-- 登录卡片 -->
    <div class="login-container">
      <div class="login-card">
        <div class="card-header">
          <h1 class="welcome-title">欢迎登录</h1>
          <p class="welcome-subtitle">驿站助手管理后台</p>
        </div>

        <div class="login-form">
          <HpsfirProForm
            :items="loginFormItems"
            hide-cancel
            hide-ok
            hide-colon
            :on-ok="handleLogin"
            ref="formRef"
            class="form-container"
          />

          <div class="form-actions">
            <HpsfirButton class="login-button" :disabled="loading" @click="submit">
              <span class="button-icon">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round"
                     stroke-linejoin="round" aria-hidden="true">
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              {{ loading ? '登录中…' : '登录' }}
            </HpsfirButton>
          </div>
        </div>

        <div class="card-footer">
          <p class="footer-text">安全登录 · 数据保护</p>
          <div class="login-version">
            <span class="version-main">{{ versionText }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧装饰 -->
    <div class="side-decoration">
      <div class="tech-circles">
        <div class="circle circle-1"></div>
        <div class="circle circle-2"></div>
        <div class="circle circle-3"></div>
      </div>
      <div class="tech-lines">
        <div class="line line-1"></div>
        <div class="line line-2"></div>
        <div class="line line-3"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>

/**
 * 品牌登录页（1.0.0 主题体系下的 LoginView 覆写）。
 *
 * 1.0.0 起框架不再暴露 loginCnt 选项，登录页改为「主题组件」：
 * 自定义主题 extend 内置主题后，用 registerThemeComponents 覆写同名组件即可。
 * 这里保留原有视觉设计（蓝金渐变背景 + 装饰粒子 + 卡片），
 * 仅把 Element Plus 的按钮/图标换成框架自带的 HpsfirButton。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
// 注意：Hpsfir* 组件一律**不要手动 import**。
// 框架会把组件按 __name 全局注册，并且**主题会用自己的实现再注册一遍**
// （registerUiComponents → registerProComponents → registerThemeComponents，
//  同名后者覆盖前者）。手动 import 拿到的是基座/默认主题那份，
// 会绕过主题覆盖 —— 页面要用主题组件就只能靠全局注册。
// 这里只 import 非组件的东西：函数（message）、组合式函数、以及类型。
import {
  useBaseUserStore,
  useRoute,
  useRouter,
  type HpsfirProFormInstance,
  type HpsfirProFormItem,
} from 'hpsfir-managebase-ui';

const baseUserStore = useBaseUserStore();
const router = useRouter();
const route = useRoute();
const loading = ref(false);

const versionText = computed(() => {
  const version = process.env.VUE_APP_PACKAGE_VERSION || '1.0.0';
  const env = process.env.VUE_APP_VERSION === 'test' ? '测试版' : '正式版';
  return `v${version} · ${env}`;
});

// 用框架导出的实例类型，不要用 InstanceType<typeof 组件>：组件已不再手动 import
// （手动 import 会绕过主题覆盖），脚本里拿不到组件值就推不出类型。
const formRef = ref<HpsfirProFormInstance>();
const loginFormItems: HpsfirProFormItem[] = [
  {
    // 1.0.0 起后端登录参数由 userNameOrPhone 改为 account（用户名/邮箱/手机号三合一）
    name: 'account',
    type: 'string',
    label: '账号',
    placeholder: '请输入用户名或手机号',
    rule: [{ required: true, message: '请输入账号' }],
  },
  {
    name: 'password',
    type: 'password',
    label: '密码',
    placeholder: '请输入密码',
    rule: [{ required: true, message: '请输入密码' }],
  },
];

const handleLogin = async(data: any) => {
  loading.value = true;
  try {
    await baseUserStore.login(data.account, data.password);
    if (route.query.back) {
      await router.replace(route.query.back as string);
      return;
    }
    await router.replace('/');
  } finally {
    loading.value = false;
  }
};

const submit = () => {
  formRef.value?.submit();
};

// 回车提交（原先依赖 @vueuse/core 的 onKeyStroke；
// 该包是 Element Plus 的传递依赖，随其移除而不可用，这里用原生监听替代）
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    formRef.value?.submit();
  }
};

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style lang="scss" scoped>
@import "./login.scss";
</style>
